import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Toaster, toast } from "react-hot-toast";
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button as MuiButton,
    Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGenericFAQs } from '../../Redux/Reducers/FAQReducer';
import { usePermissions } from '../../context/AuthContext';

function GenericFAQList() {
    const { can } = usePermissions();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [tabValue, setTabValue] = useState("user");

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const pagination = useSelector((state) => state?.FAQReducer?.paginationGeneric);
    const allFaqs = useSelector((state) => state?.FAQReducer?.genericFAQs || []);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterExcludeHiddenColumns: true,
        quickFilterValues: [''],
    });

    useEffect(() => {
        const { page, pageSize } = paginationModel;
        dispatch(fetchGenericFAQs({ page: page + 1, limit: pageSize, forValue: tabValue }));
    }, [paginationModel, dispatch, tabValue]);

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedUserId(null);
    };

    const handleDelete = async () => {
        if (selectedUserId) {
            try {
                const token = localStorage.getItem("vedhvidhi-admin-token");
                await axios.delete(`${process.env.REACT_APP_BASEURL}/admin/faq/${selectedUserId}`, {
                    headers: { Authorization: `${token}` },
                });
                toast.success("FAQ deleted successfully!");
                const { page, pageSize } = paginationModel;
                dispatch(fetchGenericFAQs({ page: page + 1, limit: pageSize, forValue: tabValue }));
                handleClose();
            } catch (error) {
                console.error("Error deleting FAQ:", error);
                toast.error("Failed to delete FAQ!");
            }
        }
    };

    const handleTabChange = (e, newValue) => {
        setTabValue(newValue);
        setPaginationModel({ page: 0, pageSize: 10 });
    };

    const columns = [
        { field: 'serialNo', headerName: 'S.No', width: 100 },
        { field: 'question', headerName: 'Question', width: 300 },
        { field: 'answer', headerName: 'Answer', width: 300 },
        { field: 'createdAt', headerName: 'Created At', width: 200 },
        { field: 'updatedAt', headerName: 'Updated At', width: 200 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ row }) => (
                <div>
                    {can('generic_faqs:edit') && (
                        <Tooltip title="Edit FAQ">
                            <IconButton component={Link} to={`/faqger-edit/${row?.parentId}`}>
                                <i className="icofont-edit text-info"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                    {can('generic_faqs:delete') && (
                        <Tooltip title="Delete FAQ">
                            <IconButton component="button" onClick={() => handleClickOpen(row?.parentId)}>
                                <i className="icofont-ui-delete text-danger"></i>
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ];

    const rows = allFaqs.flatMap((item, index) =>
        item?.faqEntries?.map((entry) => ({
            serialNo: paginationModel.page * paginationModel.pageSize + index + 1,
            question: entry?.question,
            answer: entry?.answer,
            id: entry?._id,
            parentId: item?._id,
            createdAt: item.createdAt ? new Date(item.createdAt).toLocaleString() : "",
            updatedAt: item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "",
        }))
    );

    return (
        <>
            <Toaster />
            <div className="body d-flex py-1">
                <div className="container-xxl">
                    <PageHeader1
                        pagetitle="Generic FAQ List"
                        righttitle="Add FAQ"
                        link="/generic-add"
                        routebutton={can('generic_faqs:add')}
                    />

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={tabValue} onChange={handleTabChange}>
                            <Tab label="User" value="user" />
                            <Tab label="Astro" value="astro" />
                        </Tabs>
                    </Box>

                    <div className="row g-0 mb-3">
                        <div className="col-md-12">
                            <div className="card">
                                <div className="card-body">
                                    <Box sx={{ width: 1 }}>
                                        <Box sx={{ height: 500 }}>
                                            <DataGrid
                                                rows={rows}
                                                columns={columns}
                                                getRowId={(row) => row.id}
                                                slots={{ toolbar: GridToolbar }}
                                                filterModel={filterModel}
                                                onFilterModelChange={(newModel) => setFilterModel(newModel)}
                                                slotProps={{ toolbar: { showQuickFilter: true } }}
                                                columnVisibilityModel={columnVisibilityModel}
                                                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                                                paginationMode="server"
                                                rowCount={pagination?.totalDocs || 0}
                                                pageSizeOptions={[10, 25, 50, 100]}
                                                paginationModel={paginationModel}
                                                onPaginationModelChange={setPaginationModel}
                                                disableRowSelectionOnClick
                                            />
                                        </Box>
                                    </Box>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Dialog open={open} onClose={handleClose}>
                    <DialogTitle>Delete Confirmation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>Are you sure you want to delete this FAQ?</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <MuiButton onClick={handleClose}>
                            Cancel
                        </MuiButton>
                        <MuiButton onClick={handleDelete} color="error">
                            Delete
                        </MuiButton>
                    </DialogActions>
                </Dialog>
            </div>
        </>
    );
}

export default GenericFAQList;